import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Heart, ChevronLeft, ChevronRight, Check } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import { adoptionsApi, formatApiError } from "@/lib/api";

const CAT_LABEL = {
    handicap: "Handicap-Tauben",
    flugfaehig: "Flugfähige Tauben",
    partner_gesucht: "Partner gesucht",
};

export default function AdoptionDetail() {
    const { id } = useParams();
    const [a, setA] = useState(null);
    const [loading, setLoading] = useState(true);
    const [images, setImages] = useState([]); // dataUrls
    const [idx, setIdx] = useState(0);
    const [sent, setSent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        sender_name: "",
        sender_email: "",
        sender_phone: "",
        message: "",
    });

    useEffect(() => {
        let alive = true;
        adoptionsApi
            .get(id)
            .then(async (d) => {
                if (!alive) return;
                setA(d);
                const imgs = [];
                for (let i = 0; i < (d.image_count || 0); i++) {
                    try {
                        const r = await adoptionsApi.image(id, i);
                        if (r?.data) imgs.push(r.data);
                    } catch {
                        /* skip */
                    }
                }
                if (alive) setImages(imgs);
            })
            .catch(() => {})
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [id]);

    async function send(e) {
        e.preventDefault();
        if (!form.sender_name || !form.sender_email || !form.message) {
            return toast.error("Bitte Pflichtfelder ausfüllen");
        }
        setSubmitting(true);
        try {
            await adoptionsApi.contact(id, form);
            setSent(true);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    }

    if (loading) {
        return (
            <SiteLayout>
                <div className="max-w-3xl mx-auto px-4 py-24 text-[#94a3b8] label gentle-pulse">
                    Wird geladen…
                </div>
            </SiteLayout>
        );
    }
    if (!a) {
        return (
            <SiteLayout>
                <div className="max-w-3xl mx-auto px-4 py-24 text-center">
                    <h1 className="font-display text-3xl mb-4">Nicht gefunden</h1>
                    <Link to="/vermittlung" className="btn-secondary inline-flex">
                        <ArrowLeft size={14} /> Zurück
                    </Link>
                </div>
            </SiteLayout>
        );
    }

    return (
        <SiteLayout>
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <Link
                    to="/vermittlung"
                    className="inline-flex items-center gap-2 text-[#94a3b8] hover:text-[#f0c674] text-sm mb-10"
                    data-testid="adoption-back"
                >
                    <ArrowLeft size={14} /> Zurück zur Vermittlung
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Carousel */}
                    <div className="lg:col-span-7">
                        <div className="relative aspect-[4/5] sm:aspect-[5/4] glass overflow-hidden">
                            {images.length > 0 ? (
                                <>
                                    <img
                                        src={images[idx]}
                                        alt={a.title}
                                        className="w-full h-full object-cover"
                                        data-testid="adoption-image"
                                    />
                                    {images.length > 1 && (
                                        <>
                                            <button
                                                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#040710]/80 border border-white/10 flex items-center justify-center hover:border-[#f0c674]"
                                                onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
                                                aria-label="Vorheriges Bild"
                                            >
                                                <ChevronLeft size={18} />
                                            </button>
                                            <button
                                                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#040710]/80 border border-white/10 flex items-center justify-center hover:border-[#f0c674]"
                                                onClick={() => setIdx((i) => (i + 1) % images.length)}
                                                aria-label="Nächstes Bild"
                                            >
                                                <ChevronRight size={18} />
                                            </button>
                                            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                                                {images.map((_, i) => (
                                                    <span
                                                        key={i}
                                                        className={`w-1.5 h-1.5 rounded-full ${
                                                            i === idx ? "bg-[#f0c674]" : "bg-white/30"
                                                        }`}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <Heart size={36} className="text-[#f0c674]/40" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Profile */}
                    <div className="lg:col-span-5">
                        <div className="flex flex-wrap gap-2 mb-4">
                            <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-[#f0c674]/10 text-[#f0c674] border border-[#f0c674]/30">
                                {CAT_LABEL[a.category] || a.category}
                            </span>
                            {a.flight_capable && (
                                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                                    Flugfähig
                                </span>
                            )}
                            {a.sex && a.sex !== "unbekannt" && (
                                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 text-[#94a3b8] border border-white/10">
                                    {a.sex === "m" ? "Männlich" : "Weiblich"}
                                </span>
                            )}
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl mb-4 leading-tight" data-testid="adoption-title">
                            {a.title}
                        </h1>
                        {(a.location || a.plz) && (
                            <div className="flex items-center gap-1.5 text-sm text-[#94a3b8] mb-6">
                                <MapPin size={14} />
                                {a.plz ? `${a.plz} ` : ""}
                                {a.location || ""}
                            </div>
                        )}
                        <p className="text-[#94a3b8] leading-relaxed mb-6 whitespace-pre-wrap">
                            {a.description}
                        </p>
                        {a.health_status && (
                            <div className="glass p-4 mb-6">
                                <div className="label mb-2">Gesundheit</div>
                                <p className="text-sm text-[#94a3b8]">{a.health_status}</p>
                            </div>
                        )}

                        {/* Contact form */}
                        {sent ? (
                            <div className="glass p-6 text-center" data-testid="adoption-contact-success">
                                <Check size={24} className="text-emerald-300 mx-auto mb-3" />
                                <h3 className="font-display text-xl mb-2">Nachricht gesendet</h3>
                                <p className="text-[#94a3b8] text-sm">
                                    Die Pflegestelle meldet sich bei dir per E-Mail.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={send} className="glass p-6 space-y-4" data-testid="adoption-contact-form">
                                <div className="label">Kontaktformular</div>
                                <input
                                    value={form.sender_name}
                                    onChange={(e) => setForm((f) => ({ ...f, sender_name: e.target.value }))}
                                    placeholder="Dein Name *"
                                    className="input-field"
                                    required
                                    data-testid="contact-name"
                                />
                                <input
                                    type="email"
                                    value={form.sender_email}
                                    onChange={(e) => setForm((f) => ({ ...f, sender_email: e.target.value }))}
                                    placeholder="E-Mail *"
                                    className="input-field"
                                    required
                                    data-testid="contact-email"
                                />
                                <input
                                    value={form.sender_phone}
                                    onChange={(e) => setForm((f) => ({ ...f, sender_phone: e.target.value }))}
                                    placeholder="Telefon (optional)"
                                    className="input-field"
                                    data-testid="contact-phone"
                                />
                                <textarea
                                    value={form.message}
                                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                                    placeholder="Deine Nachricht *"
                                    className="input-field"
                                    rows={5}
                                    required
                                    data-testid="contact-message"
                                />
                                <button type="submit" className="btn-primary" disabled={submitting} data-testid="contact-submit">
                                    {submitting ? "Wird gesendet…" : "Nachricht senden"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </SiteLayout>
    );
}
